import { useEffect, useRef, useState } from "react";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import type { Testimonial } from "../hooks/useTestimonials";

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export const TestimonialCard = ({ testimonial }: TestimonialCardProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [displayedTestimonial, setDisplayedTestimonial] = useState(testimonial);
  const fadeInTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsVisible(false);
    const fadeOutTimeout = setTimeout(() => {
      setDisplayedTestimonial(testimonial);
      fadeInTimeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 20);
    }, 1000);

    return () => {
      clearTimeout(fadeOutTimeout);
      if (fadeInTimeoutRef.current) clearTimeout(fadeInTimeoutRef.current);
    };
  }, [testimonial]);

  const { image, imageVariants, author, content, role } = displayedTestimonial;

  return (
    <div className="flex min-h-[300px] w-full justify-center" key={author}>
      <div
        className={`flex w-full max-w-[424px] flex-col items-center space-y-6 text-center transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div className="relative h-20 w-20 overflow-hidden rounded-full">
          <img
            loading="lazy"
            src={imageVariants.large || image}
            srcSet={`
              ${imageVariants.thumbnail} 40w,
              ${imageVariants.small} 80w,
              ${imageVariants.medium} 160w,
              ${imageVariants.large} 320w
            `}
            sizes="(max-width: 80px) 40px, (max-width: 160px) 80px, (max-width: 320px) 160px, 320px"
            alt={author}
            className="h-full w-full scale-110 object-cover"
          />
        </div>
        <div className="flex-1">
          <div className="opacity-70">
            <Typography.BodyMd color="text-white">{content}</Typography.BodyMd>
          </div>
          <div className="mt-4">
            <div className="opacity-90">
              <Typography.LabelMd weight="semibold" color="text-white">
                {author}
              </Typography.LabelMd>
            </div>
            <div className="opacity-50">
              <Typography.LabelMd weight="semibold" color="text-white">
                {role}
              </Typography.LabelMd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
